/** RTK Query API отдельного сервиса миграции пользовательских данных. */
import { config } from 'config';
import { api } from 'services/api';

export type BulkMigrationProcessStatus = 'idle' | 'running' | 'stopping';

export interface MigrationProcessStatus {
  status: BulkMigrationProcessStatus;
  bulkStatus: BulkMigrationProcessStatus;
  activeUser: {
    username: string;
    user_id: string;
  } | null;
  stage: MigrationProcessStage | null;
  pipeline: MigrationPipelineState;
  changes: MigrationUserChanges;
}

export interface MigrationPipelineSlot {
  user: NonNullable<MigrationProcessStatus['activeUser']>;
  stage: MigrationProcessStage;
}

export interface MigrationPipelineState {
  dynamodb: MigrationPipelineStageState;
  postgres: MigrationPipelineStageState;
  minio: MigrationPipelineStageState;
}

export interface MigrationPipelineStageState {
  active: MigrationPipelineSlot | null;
  waiting: Array<NonNullable<MigrationProcessStatus['activeUser']>>;
}

export type MigrationProcessStage =
  | 'collecting_devices'
  | 'preparing_workspace'
  | 'exporting_state'
  | 'exporting_history'
  | 'importing_state'
  | 'synchronizing_telemetry_counters'
  | 'replacing_monotonic_telemetry'
  | 'importing_history'
  | 'uploading_backup'
  | 'updating_progress'
  | 'cleaning_up'
  | 'finalizing';

export type BulkEstimateProcessStatus = BulkMigrationProcessStatus;

export interface MigrationEstimateProcessStatus {
  status: BulkEstimateProcessStatus;
  bulkStatus: BulkEstimateProcessStatus;
  activeUser: MigrationProcessStatus['activeUser'];
  changes: MigrationUserChanges;
}

export interface MigrationUserChanges {
  generation: string;
  revision: number;
  changedUserIds: string[];
  resetRequired: boolean;
}

export interface MigrationStatusQuery {
  estimateChangesAfterRevision?: number;
  estimateChangesGeneration?: string;
  migrationChangesAfterRevision?: number;
  migrationChangesGeneration?: string;
}

export interface BulkEstimateProcessState {
  status: BulkEstimateProcessStatus;
}

export type BulkMigrationProcessState = BulkEstimateProcessState;

export interface MigrationStatus {
  service: string;
  status: 'idle' | 'running';
  estimate: MigrationEstimateProcessStatus;
  migration: MigrationProcessStatus;
  podResources?: {
    podName: string;
    cpuUtilizationPercent: number;
    memoryFreeBytes: number;
    memoryTotalBytes: number;
    diskFreeBytes: number;
    diskTotalBytes: number;
  };
  dynamodbRcuConsumedPerSecond?: number;
  dynamodbRcuLimitPerSecond?: number;
  dynamodbTargetRcuPerSecond?: number;
  dynamodbActiveQueries?: number;
  dynamodbQueuedQueries?: number;
  dynamodbParallelQueryLimit?: number;
  dynamodbQueryPageItemLimit?: number;
  dynamodbEstimatedPageRcu?: number;
  dynamodbThrottleCount?: number;
}

export interface MigrationSummary {
  total_users_count: number;
  users_with_devices_count: number;
  total_devices_count: number;
  migrated_users_count: number;
  estimated_users_count: number;
  total_estimate_duration_ms: number;
  total_migration_duration_ms: number;
  estimated_parquet_size_mb: number;
  estimated_postgres_growth_mb: number;
  estimated_parquet_size_with_six_month_telemetry_mb: number;
  estimated_postgres_growth_with_six_month_telemetry_mb: number;
}

export interface MigrationDeviceCountDistributionPoint {
  deviceCount: number;
  usersCount: number;
}

export type MigrationVisualizationMetric = 'count' | 'parquet' | 'postgres';

export type MigrationDataCategory = 'sales' | 'encash' | 'events' | 'telemetry';

export interface MigrationDataCompositionValue {
  count: number;
  parquetMb: number;
  postgresMb: number;
}

export interface MigrationDataCompositionScenario {
  scenario: 'full' | 'telemetry_divided_by_5' | 'telemetry_divided_by_10';
  telemetryDivisor: 1 | 5 | 10;
  total: MigrationDataCompositionValue;
  categories: Array<
    MigrationDataCompositionValue & { category: MigrationDataCategory }
  >;
}

export interface MigrationVisualization {
  deviceCountDistribution: MigrationDeviceCountDistributionPoint[];
  dataComposition: MigrationDataCompositionScenario[];
}

export type MigrationUserStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'failed';

export type MigrationUserEstimateStatus =
  | 'not_started'
  | 'partial_update_required'
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'failed';

export interface MigrationUserEstimateState {
  user_id: string;
  estimate_status: MigrationUserEstimateStatus;
  estimate_requested_at: string | null;
  estimate_started_at: string | null;
  estimate_finished_at: string | null;
  estimate_last_error: string | null;
}

export interface MigrationUser extends MigrationUserEstimateState {
  username: string;
  user_id: string;
  status: MigrationUserStatus;
  device_count: number | null;
  estimated_sales_count: number | null;
  estimated_encash_count: number | null;
  estimated_events_count: number | null;
  estimated_telemetry_count: number | null;
  estimated_telemetry_last_six_months_count: number | null;
  estimated_telemetry_mono_count: number | null;
  estimated_telemetry_mono_last_six_months_count: number | null;
  estimated_parquet_size_mb: number | null;
  estimated_postgres_growth_mb: number | null;
  estimated_parquet_size_with_six_month_telemetry_mb: number | null;
  estimated_postgres_growth_with_six_month_telemetry_mb: number | null;
  started_at: string | null;
  finished_at: string | null;
  processing_duration_ms: number;
  dynamodb_duration_ms: number;
  postgres_duration_ms: number;
  minio_duration_ms: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface MigrationUserMigrationState {
  username: string;
  user_id: string;
  status: MigrationUserStatus;
  started_at: string | null;
  finished_at: string | null;
  processing_duration_ms: number;
  dynamodb_duration_ms: number;
  postgres_duration_ms: number;
  minio_duration_ms: number;
  last_error: string | null;
  force: boolean;
}

export interface MigrationDevice {
  device_id: string;
  user_id: string;
  status: MigrationUserStatus;
  estimate_status: MigrationUserEstimateStatus;
  estimated_sales_count: number | null;
  estimated_encash_count: number | null;
  estimated_events_count: number | null;
  estimated_telemetry_count: number | null;
  estimated_telemetry_last_six_months_count: number | null;
  estimated_telemetry_mono_count: number | null;
  estimated_telemetry_mono_last_six_months_count: number | null;
  estimated_parquet_size_mb: number | null;
  estimated_postgres_growth_mb: number | null;
  estimated_parquet_size_with_six_month_telemetry_mb: number | null;
  estimated_postgres_growth_with_six_month_telemetry_mb: number | null;
  estimate_started_at: string | null;
  estimate_finished_at: string | null;
  estimate_last_error: string | null;
  started_at: string | null;
  finished_at: string | null;
  processing_duration_ms: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  prev_user_id: string | null;
}

export type MigrationDeviceJson =
  | string
  | number
  | boolean
  | null
  | MigrationDeviceJson[]
  | { [key: string]: MigrationDeviceJson };

export interface MigrationDeviceComparison {
  deviceId: string;
  dynamodb: MigrationDeviceJson;
  postgres: MigrationDeviceJson;
}

export interface MigrationDevicesPage {
  items: MigrationDevice[];
  total: number;
  limit: number;
  offset: number;
}

export interface MigrationDevicesQuery {
  search: string;
  limit: number;
  offset: number;
  sortBy: MigrationTableSortField;
  sortDirection: MigrationSortDirection;
}

export type MigrationSortDirection = 'asc' | 'desc';

export type MigrationTableSortField =
  | 'device_id'
  | 'user_id'
  | 'username'
  | 'estimate_status'
  | 'status'
  | 'device_count'
  | 'estimated_sales_count'
  | 'estimated_encash_count'
  | 'estimated_events_count'
  | 'estimated_telemetry_count'
  | 'estimated_telemetry_mono_count'
  | 'estimated_parquet_size_mb'
  | 'estimated_postgres_growth_mb'
  | 'estimated_telemetry_last_six_months_count'
  | 'estimated_telemetry_mono_last_six_months_count'
  | 'estimated_parquet_size_with_six_month_telemetry_mb'
  | 'estimated_postgres_growth_with_six_month_telemetry_mb'
  | 'estimate_timing'
  | 'estimate_last_error'
  | 'migration_timing'
  | 'last_error'
  | 'prev_user_id';

export interface SyncMigrationUsersResult {
  synchronizedCount: number;
}

export interface ResetMigrationUsersStatusResult {
  updatedCount: number;
  updatedUserIds: string[];
}

export interface SyncMigrationDevicesResult {
  synchronizedCount: number;
  addedCount: number;
  reassignedCount: number;
  removedCount: number;
}

export interface DynamoDbRcuLimitConfig {
  rcuLimitPerSecond: number;
}

function getAuthorizationHeaders() {
  return {
    // Migrator принимает тот же access-token, который выдал portal.
    Authorization: localStorage.getItem('token') ?? ''
  };
}

export const migrationsApi = api
  .enhanceEndpoints({
    addTagTypes: [
      'MigrationUsers',
      'MigrationDevices',
      'MigrationSummary',
      'MigrationVisualization',
      'MigrationStatus',
      'DynamoDbRcuLimit'
    ]
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getMigrationStatus: build.query<MigrationStatus, MigrationStatusQuery>({
        query: ({
          estimateChangesAfterRevision,
          estimateChangesGeneration,
          migrationChangesAfterRevision,
          migrationChangesGeneration
        }) => ({
          url: `${config.MIGRATOR_API}/migration/status`,
          method: 'GET',
          headers: getAuthorizationHeaders(),
          params: {
            estimateChangesAfterRevision,
            estimateChangesGeneration,
            migrationChangesAfterRevision,
            migrationChangesGeneration
          }
        }),
        providesTags: ['MigrationStatus']
      }),
      startBulkEstimate: build.mutation<BulkEstimateProcessState, void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/estimate/start`,
          method: 'POST',
          headers: getAuthorizationHeaders()
        }),
        invalidatesTags: ['MigrationStatus']
      }),
      stopBulkEstimate: build.mutation<BulkEstimateProcessState, void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/estimate/stop`,
          method: 'POST',
          headers: getAuthorizationHeaders()
        }),
        invalidatesTags: ['MigrationStatus']
      }),
      startBulkMigration: build.mutation<BulkMigrationProcessState, void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/migrate/start`,
          method: 'POST',
          headers: getAuthorizationHeaders()
        }),
        invalidatesTags: ['MigrationStatus']
      }),
      stopBulkMigration: build.mutation<BulkMigrationProcessState, void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/migrate/stop`,
          method: 'POST',
          headers: getAuthorizationHeaders()
        }),
        invalidatesTags: ['MigrationStatus']
      }),
      getMigrationSummary: build.query<MigrationSummary, void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/summary`,
          method: 'GET',
          headers: getAuthorizationHeaders()
        }),
        providesTags: ['MigrationSummary']
      }),
      getMigrationVisualization: build.query<MigrationVisualization, void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/visualization`,
          method: 'GET',
          headers: getAuthorizationHeaders()
        }),
        providesTags: ['MigrationVisualization']
      }),
      getMigrationUsers: build.query<MigrationUser[], void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/users`,
          method: 'GET',
          headers: getAuthorizationHeaders()
        }),
        providesTags: ['MigrationUsers']
      }),
      getMigrationUser: build.query<MigrationUser, string>({
        query: (userId) => ({
          url: `${config.MIGRATOR_API}/migration/users/${encodeURIComponent(userId)}`,
          method: 'GET',
          headers: getAuthorizationHeaders()
        })
      }),
      getMigrationDevices: build.query<
        MigrationDevicesPage,
        MigrationDevicesQuery
      >({
        query: ({ search, limit, offset, sortBy, sortDirection }) => ({
          url: `${config.MIGRATOR_API}/migration/devices`,
          method: 'GET',
          headers: getAuthorizationHeaders(),
          params: { search, limit, offset, sortBy, sortDirection }
        }),
        providesTags: ['MigrationDevices']
      }),
      getMigrationDeviceComparison: build.query<
        MigrationDeviceComparison,
        string
      >({
        query: (deviceId) => ({
          url: `${config.MIGRATOR_API}/migration/devices/${encodeURIComponent(deviceId)}/compare`,
          method: 'GET',
          headers: getAuthorizationHeaders()
        })
      }),
      getDynamoDbRcuLimit: build.query<DynamoDbRcuLimitConfig, void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/config/dynamodb/rcu-limit`,
          method: 'GET',
          headers: getAuthorizationHeaders()
        }),
        providesTags: ['DynamoDbRcuLimit']
      }),
      updateDynamoDbRcuLimit: build.mutation<DynamoDbRcuLimitConfig, number>({
        query: (rcuLimitPerSecond) => ({
          url: `${config.MIGRATOR_API}/migration/config/dynamodb/rcu-limit`,
          method: 'PUT',
          headers: getAuthorizationHeaders(),
          data: { rcuLimitPerSecond }
        }),
        invalidatesTags: ['DynamoDbRcuLimit']
      }),
      syncMigrationUsers: build.mutation<SyncMigrationUsersResult, void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/users/sync`,
          method: 'POST',
          headers: getAuthorizationHeaders()
        }),
        // Повторно запрашиваем список, только если в него добавились пользователи.
        invalidatesTags: (result) =>
          result && result.synchronizedCount > 0
            ? ['MigrationUsers', 'MigrationSummary', 'MigrationVisualization']
            : []
      }),
      resetMigrationUsersStatus: build.mutation<
        ResetMigrationUsersStatusResult,
        string[]
      >({
        query: (userIds) => ({
          url: `${config.MIGRATOR_API}/migration/users/actions/reset-migration-status`,
          method: 'POST',
          headers: getAuthorizationHeaders(),
          data: { userIds }
        }),
        invalidatesTags: ['MigrationSummary']
      }),
      syncMigrationDevices: build.mutation<SyncMigrationDevicesResult, void>({
        query: () => ({
          url: `${config.MIGRATOR_API}/migration/devices/sync`,
          method: 'POST',
          headers: getAuthorizationHeaders()
        }),
        // Любое изменение состава аппаратов делает оценки владельцев частично
        // устаревшими, поэтому обновляем таблицы и агрегированные данные.
        invalidatesTags: (result) =>
          result && result.synchronizedCount > 0
            ? [
                'MigrationDevices',
                'MigrationUsers',
                'MigrationSummary',
                'MigrationVisualization'
              ]
            : []
      }),
      estimateMigrationUser: build.mutation<MigrationUserEstimateState, string>(
        {
          query: (userId) => ({
            url: `${config.MIGRATOR_API}/migration/users/${encodeURIComponent(userId)}/estimate`,
            method: 'POST',
            headers: getAuthorizationHeaders()
          })
        }
      ),
      migrateMigrationUser: build.mutation<
        MigrationUserMigrationState,
        { userId: string; force?: boolean }
      >({
        query: ({ userId, force = false }) => ({
          url: `${config.MIGRATOR_API}/migration/users/${encodeURIComponent(userId)}/migrate`,
          method: 'POST',
          headers: getAuthorizationHeaders(),
          params: force ? { force: true } : undefined
        }),
        invalidatesTags: ['MigrationStatus']
      }),
      getMigrationUserEstimateStatus: build.query<
        MigrationUserEstimateState,
        string
      >({
        query: (userId) => ({
          url: `${config.MIGRATOR_API}/migration/users/${encodeURIComponent(userId)}/estimate/status`,
          method: 'GET',
          headers: getAuthorizationHeaders()
        })
      })
    })
  });

export const {
  useGetMigrationStatusQuery,
  useStartBulkEstimateMutation,
  useStopBulkEstimateMutation,
  useStartBulkMigrationMutation,
  useStopBulkMigrationMutation,
  useGetMigrationSummaryQuery,
  useGetMigrationVisualizationQuery,
  useGetMigrationUsersQuery,
  useLazyGetMigrationUserQuery,
  useGetMigrationDevicesQuery,
  useGetMigrationDeviceComparisonQuery,
  useGetDynamoDbRcuLimitQuery,
  useLazyGetMigrationUserEstimateStatusQuery,
  useSyncMigrationUsersMutation,
  useResetMigrationUsersStatusMutation,
  useSyncMigrationDevicesMutation,
  useEstimateMigrationUserMutation,
  useMigrateMigrationUserMutation,
  useUpdateDynamoDbRcuLimitMutation
} = migrationsApi;
